'use client';

import React from 'react';
import type { Platform, ContentType } from '@/lib/rateCalculator';
import { PLATFORM_CONTENT_TYPES } from '@/lib/rateCalculator';
import { Image, Film, BookOpen, Video, Zap } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

interface ContentTypeSelectorProps {
  platform: Platform;
  selected: ContentType;
  onChange: (ct: ContentType) => void;
}

const contentTypeIcons: Record<ContentType, React.ReactNode> = {
  post: <Image size={16} />,
  reel: <Film size={16} />,
  story: <BookOpen size={16} />,
  video: <Video size={16} />,
  short: <Zap size={16} />,
};

const contentTypeMultiplierLabels: Record<ContentType, string> = {
  post: '1.0×',
  reel: '1.8×',
  story: '0.4×',
  video: '2.5×',
  short: '1.4×',
};

export default function ContentTypeSelector({ platform, selected, onChange }: ContentTypeSelectorProps) {
  const available = PLATFORM_CONTENT_TYPES[platform];
  const { t } = useLanguage();

  const contentTypeLabels: Record<ContentType, string> = {
    post: t.contentTypes.post,
    reel: t.contentTypes.reel,
    story: t.contentTypes.story,
    video: t.contentTypes.video,
    short: t.contentTypes.short,
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-foreground mb-3 tracking-wide">
        {t.form.contentType}
        <span className="ml-2 text-xs text-muted-foreground font-normal">
          {t.form.contentTypeHint}
        </span>
      </label>
      <div className="flex flex-wrap gap-2">
        {available.map((ct) => (
          <button
            key={`content-type-${ct}`}
            type="button"
            onClick={() => onChange(ct)}
            className={`content-type-pill flex items-center gap-2 px-3 py-2 ${
              selected === ct ? 'selected' : ''
            }`}
          >
            <span className={selected === ct ? 'text-accent' : 'text-muted-foreground'}>
              {contentTypeIcons[ct]}
            </span>
            <span className="text-xs font-semibold text-foreground">
              {contentTypeLabels[ct]}
            </span>
            <span
              className={`text-[10px] font-bold font-tabular ml-1 ${
                selected === ct ? 'text-accent' : 'text-muted-foreground'
              }`}
            >
              {contentTypeMultiplierLabels[ct]}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}